import { Navigate, Outlet, matchPath, useLocation, type Location, type Params, type Path } from 'react-router-dom'
import { useAuth } from './AuthContext'
import type { Role } from '@/lib/types'

/** /eng pages that show what an /sme page shows under a different path; identical paths need no entry. */
const ENG_EQUIVALENTS: [pattern: string, to: (params: Params, search: URLSearchParams) => Partial<Path>][] = [
  // Only the program filter means the same on both lists (search fields and stage keys differ), and the engineer list remembers its filters.
  ['/sme/task-directory', (_, search) => {
    const program = search.get('program')
    return { pathname: '/eng/tasks', search: program ? `?${new URLSearchParams({ program })}` : '' }
  }],
  // Expert template links may be "new" or an unsubmitted draft, which engineers can't open; send them home.
  ['/sme/templates/:templateId', () => ({ pathname: '/eng', search: '', hash: '' })],
  ['/sme/task-directory/:taskId', ({ taskId = '' }) => ({ pathname: `/eng/tasks/${taskId}` })],
  // The draft editor has its own sections (definition, …); only brief and rubric exist on the engineer task page.
  ['/sme/tasks/:taskId/edit', ({ taskId = '' }, search) => {
    const section = search.get('section')
    return { pathname: `/eng/tasks/${taskId}`, search: section === 'brief' || section === 'rubric' ? `?section=${section}` : '' }
  }],
  ['/sme/reviews/:taskId/:roundId', ({ taskId = '' }) => ({ pathname: `/eng/tasks/${taskId}`, search: '?section=activity' })],
]

/** The engineer page for an /sme location, or the engineer dashboard when there is none. */
function engineerLocation(location: Location, engineerPaths: string[]): Partial<Path> | string {
  const target: Partial<Path> = { pathname: location.pathname.replace(/^\/sme(?=\/|$)/i, '/eng'), search: location.search, hash: location.hash }
  for (const [pattern, to] of ENG_EQUIVALENTS) {
    const match = matchPath(pattern, location.pathname)
    if (match) { Object.assign(target, to(match.params, new URLSearchParams(location.search))); break }
  }
  return engineerPaths.some(path => matchPath(path, target.pathname ?? '')) ? target : '/eng'
}

/** Shared sign-in preserves the requested location; claims still control workspace access. */
export function RequireRole({ role, engineerPaths }: { role: Role, engineerPaths?: string[] }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <div className="page"><p className="muted">Loading…</p></div>
  if (!user) return <Navigate to="/login" state={{ from: location.pathname + location.search + location.hash }} replace />
  if (user.role !== role) {
    if (user.role === null) return <div className="page"><p className="error">Your account has no role yet. Contact an engineer.</p></div>
    if (user.role === 'engineer' && engineerPaths) return <Navigate to={engineerLocation(location, engineerPaths)} replace />
    return <Navigate to={user.role === 'sme' ? '/sme' : '/eng'} replace />
  }
  return <Outlet />
}
